import axios from '@/api/axios';
import {
  VoyagerPlanner,
  VoyagerPlannerResponse,
  VoyagerPlannerPostResponse,
  VoyagerPlannerAlarm,
  VoyagerPlannerAlarmResponse,
} from '@/types/api';
import apiConfig from '@/config/url-api';

import { useMutation, UseMutationOptions, useQuery, UseQueryResult } from '@tanstack/react-query';
import { VoyagerPlannerActivateRequest, VoyagerPlannerRequest } from '@/types/form';

/**
 *
 * @returns
 * Service get all voyager
 */
export const getAllPlanners = async (): Promise<VoyagerPlanner[]> => {
  const response = await axios.get<VoyagerPlannerResponse>(apiConfig.voyager.all);
  return response.data.data;
};

/**
 *
 * @returns
 * hooks get all voyager
 */
export const useQueryAllPlanners = (): UseQueryResult<VoyagerPlanner[], Error> => {
  const vessels = useQuery({
    queryKey: ['AllVoyager'],
    queryFn: getAllPlanners,
  });

  return vessels;
};

/**
 *
 * @param params
 * @returns
 * Service create voyager
 */
export const createPlanner = async (params: VoyagerPlannerRequest): Promise<VoyagerPlanner> => {
  const response = await axios.post<VoyagerPlannerPostResponse>(apiConfig.voyager.single, params);
  return response.data.data;
};

/**
 *
 * @returns
 * Hooks create voyager
 */
export const useMutationCreatePlanner = () => {
  const routeMutation = useMutation({
    mutationFn: (data: VoyagerPlannerRequest) => createPlanner(data),
  } as UseMutationOptions<VoyagerPlanner, unknown, VoyagerPlannerRequest, unknown>);

  return routeMutation;
};

/**
 *
 * @param id
 * @param params
 * @returns
 *
 * Service update voyager
 */
export const editPlanner = async (
  id: string,
  params: VoyagerPlannerRequest,
): Promise<VoyagerPlanner> => {
  const response = await axios.put<VoyagerPlannerPostResponse>(
    apiConfig.voyager.getDetail.format({ id: id }),
    params,
  );
  return response.data.data;
};

/**
 *
 * @returns
 * Hooks update voyager
 */
export const useMutationEditPlanner = () => {
  const routeMutation = useMutation({
    mutationFn: (data: { id: string; params: VoyagerPlannerRequest }) =>
      editPlanner(data.id, data.params),
  } as UseMutationOptions<VoyagerPlanner, unknown, { id: string; params: VoyagerPlannerRequest }, unknown>);

  return routeMutation;
};

/**
 *
 * @param id
 * @param params
 * @returns
 *
 * Service update voyager
 */
export const activatePlanner = async (
  id: string,
  params: VoyagerPlannerActivateRequest,
): Promise<void> => {
  return await axios.put(apiConfig.voyager.activate.format({ id: id }), params);
};

/**
 *
 * @returns
 * Hooks update voyager
 */
export const useMutationActivateVoyager = () => {
  const routeMutation = useMutation({
    mutationFn: (data: { id: string; params: VoyagerPlannerActivateRequest }) =>
      activatePlanner(data.id, data.params),
  } as UseMutationOptions<void, unknown, { id: string; params: VoyagerPlannerActivateRequest }, unknown>);

  return routeMutation;
};

/**
 * Service delete voyager
 */
export const deletePlanner = async (id: string): Promise<void> => {
  await axios.delete(apiConfig.voyager.getDetail.format({ id: id }));
};

export const useMutationDeletePlanner = () => {
  const voyagerMutation = useMutation({
    mutationFn: (id: string) => deletePlanner(id),
  } as UseMutationOptions<void, unknown, string, unknown>);

  return voyagerMutation;
};

/**
 *
 * @returns
 * Service get all voyager
 */
export const getAlaramPlanner = async (id: string | null): Promise<VoyagerPlannerAlarm[]> => {
  const response = await axios.get<VoyagerPlannerAlarmResponse>(
    apiConfig.voyager.getAlarmVoyager.format({ id }),
  );
  return response.data.data;
};

/**
 *
 * @returns
 * hooks get alarm planner
 */
export const useQueryAlarmPlanner = (
  id: string | null,
): UseQueryResult<VoyagerPlannerAlarm[], Error> => {
  if (!id) null;

  const alarmVoyager = useQuery({
    queryKey: ['VoyagerAlarm', { id }],
    queryFn: () => getAlaramPlanner(id),
    enabled: false,
    staleTime: 1000 * 60 * 5,
  });

  return alarmVoyager;
};
